import React, { createContext, ReactNode, useContext, useState } from 'react'

interface SidebarContextType {
  open: boolean
  setOpen: (open: boolean) => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

const SIDEBAR_STORAGE_KEY = 'sidebar:open'

function getInitialSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    return stored !== 'false'
  } catch {
    return true
  }
}

export function SidebarContextProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(getInitialSidebarOpen)

  const handleSetOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    setOpen((prev) => {
      const newValue = typeof value === 'function' ? value(prev) : value
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newValue))
        } catch {
          // ignore storage errors (e.g. private mode)
        }
      }
      return newValue
    })
  }

  return (
    <SidebarContext.Provider value={{ open, setOpen: handleSetOpen }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarContextProvider')
  }
  return context
}
